import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import {
  exportDataAsFile,
  getProductUnits,
  importDataFromObject,
  resetTransactions,
  logout as authLogout,
} from "../services/dataService";
import BarcodeScanner from "./BarcodeScanner";
import Sidebar, { getSidebarMenuAction } from "./Sidebar";
import WorkspaceHeader from "./WorkspaceHeader";
import CurrencyInput from "./CurrencyInput";
import { useToast } from "../contexts/ToastContext";
import { useConfirmation } from "../contexts/ConfirmationContext";
import {
  applyReportDateScope,
  calculateReportSummary,
  filterReportTransactions,
  formatLocalDateInput,
  getVisibleReportTransactions,
} from "../utils/reportSummary";
import {
  approveSharedUnitBarcodes,
  createUnitDraft,
  findSharedUnitBarcodeConflicts,
  getBaseUnitDisplayName,
  getParentBarcodeForSave,
  getTransactionUnitSnapshot,
  normalizeProductUnits,
  shouldRequireParentBarcode,
  validateProductUnits,
} from "../utils/inventoryUnits";
import {
  getInventoryBarcodeDisplay,
  inventoryItemMatchesSearch,
} from "../utils/inventoryPresentation";
import ProductUnitsEditor from "./inventory/ProductUnitsEditor";
import PurchasesView from "./purchases/PurchasesView";
import SupplierManager from "./purchases/SupplierManager";

// Format number with commas and no decimals
const formatCurrency = (value) => {
  return Math.round(parseFloat(value) || 0).toLocaleString("en-US");
};

const formatDashboardDate = (value) => new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
}).format(new Date(value));

const PROFIT_PERIOD_OPTIONS = [
  { value: "current_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "last_3_months", label: "Last 3 months" },
  { value: "last_6_months", label: "Last 6 months" },
];

const ADMIN_PAGE_LABELS = {
  dashboard: "Dashboard",
  inventory: "Inventory",
  reports: "Sales reports",
  purchases: "Purchases",
  suppliers: "Suppliers",
};

const defaultProductUnits = (price = "") => [
  createUnitDraft({
    unit_name: "Unit",
    unit_code: "UNIT",
    conversion_factor: 1,
    selling_price: price,
    is_base_unit: true,
    can_sell: true,
    can_purchase: true,
  }),
];

function Admin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const showToast = useToast();
  const showConfirmation = useConfirmation();
  const setError = (msg) => msg && showToast(msg, "error");
  const setSuccess = (msg) => msg && showToast(msg, "success");
  const [editingItem, setEditingItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDetailsModal, setShowItemDetailsModal] = useState(false);
  const [openItemMenuId, setOpenItemMenuId] = useState(null);
  const [formData, setFormData] = useState({
    barcode: "",
    name: "",
    price: "",
    base_price: "",
    category: "",
    stock: "",
  });
  const [productUnits, setProductUnits] = useState(defaultProductUnits());
  const [showProductUnitValidation, setShowProductUnitValidation] = useState(false);
  const barcodeInputRef = useRef(null);

  const [user, setUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [userFormData, setUserFormData] = useState({
    username: "",
    password: "",
    role: "cashier",
    fullName: "",
  });
  const [statistics, setStatistics] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [profitPeriod, setProfitPeriod] = useState("current_month");
  const [profitLoading, setProfitLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCameraFallback, setShowCameraFallback] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({
    name: "",
    description: "",
  });
  const [newCategoryInput, setNewCategoryInput] = useState("");
  const [openCategoryMenuId, setOpenCategoryMenuId] = useState(null);

  const [detailsUnits, setDetailsUnits] = useState([]);

  // Statistics pagination and filters
  const [lowStockPage, setLowStockPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [lowStockFilters, setLowStockFilters] = useState({
    search: "",
    category: "",
    stockLevel: "all", // all, low, out
  });

  // Inventory items pagination and filters (with localStorage persistence)
  const [inventoryPage, setInventoryPage] = useState(() => {
    const saved = localStorage.getItem("inventoryPage");
    return saved ? parseInt(saved) : 1;
  });
  const [inventorySort, setInventorySort] = useState(() => {
    return localStorage.getItem("inventorySort") || "name";
  });
  const [inventoryFilters, setInventoryFilters] = useState(() => {
    const saved = localStorage.getItem("inventoryFilters");
    return saved ? JSON.parse(saved) : {
      search: "",
      category: "",
      stockLevel: "all",
    };
  });

  // Transactions for Reports view
  const [transactions, setTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [transactionPage, setTransactionPage] = useState(1);
  const [transactionFilters, setTransactionFilters] = useState(() => {
    const today = formatLocalDateInput();
    return {
      search: "",
      dateFrom: today,
      dateTo: today,
    };
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);

  // View state - must be declared before useEffect hooks that use it
  const [currentView, setCurrentView] = useState(() => {
    const savedView = localStorage.getItem("adminCurrentView");
    return [
      "dashboard",
      "inventory",
      "purchases",
      "suppliers",
      "reports",
    ].includes(savedView)
      ? savedView
      : "dashboard";
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const mobileMenuButtonRef = useRef(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem("sidebarCollapsed") === "true";
  });

  const handleToggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem("sidebarCollapsed", String(next));
      return next;
    });
  };

  const handleOpenMenu = () => {
    const action = getSidebarMenuAction(window.innerWidth, sidebarCollapsed);

    if (action === "open") {
      setSidebarOpen(true);
      return;
    }

    if (action === "expand") {
      handleToggleSidebar();
    }
  };

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("user") || "null");
    setUser(userData);
    fetchItems();
    fetchCategories();
    if (userData?.role === "admin") {
      fetchBackups();
      fetchUsers();
      fetchStatistics();
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch transactions when reports view is active
  useEffect(() => {
    if (currentView === "reports" && user?.role === "admin") {
      fetchTransactions();
    }
  }, [currentView, user?.role]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save inventory state to localStorage
  useEffect(() => {
    localStorage.setItem("inventoryPage", String(inventoryPage));
  }, [inventoryPage]);

  useEffect(() => {
    localStorage.setItem("inventorySort", inventorySort);
  }, [inventorySort]);

  useEffect(() => {
    localStorage.setItem("inventoryFilters", JSON.stringify(inventoryFilters));
  }, [inventoryFilters]);

  // Save current view to localStorage
  useEffect(() => {
    localStorage.setItem("adminCurrentView", currentView);
  }, [currentView]);

  useEffect(() => {
    if (openItemMenuId === null) return;

    const handleOutsideClick = (event) => {
      const menuRoot = event.target.closest("[data-inventory-item-menu]");
      if (!menuRoot) {
        setOpenItemMenuId(null);
        return;
      }

      const menuId = menuRoot.getAttribute("data-inventory-item-menu");
      if (menuId !== String(openItemMenuId)) setOpenItemMenuId(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openItemMenuId]);

  useEffect(() => {
    if (openCategoryMenuId === null) return;

    const handleOutsideClick = (event) => {
      const menuRoot = event.target.closest("[data-category-menu]");
      if (!menuRoot) {
        setOpenCategoryMenuId(null);
        return;
      }

      const menuId = menuRoot.getAttribute("data-category-menu");
      if (menuId !== String(openCategoryMenuId)) setOpenCategoryMenuId(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openCategoryMenuId]);

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (err) {
      setError("Failed to fetch categories");
    }
  };

  const fetchStatistics = async (nextProfitPeriod = profitPeriod) => {
    setStatsLoading(true);
    try {
      const response = await api.get("/statistics", {
        profitPeriod: nextProfitPeriod,
      });
      setStatistics(response.data);
    } catch (err) {
      setError("Failed to fetch statistics");
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (err) {
      setError("Failed to fetch users");
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);

    try {
      await api.post("/users", userFormData);
      setSuccess("User created successfully");
      setUserFormData({
        username: "",
        password: "",
        role: "cashier",
        fullName: "",
      });
      setShowUserModal(false);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create user");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userId) => {
    showConfirmation(
      "Delete User",
      "Are you sure you want to delete this user? This action cannot be undone.",
      async () => {
        try {
          await api.delete(`/users/${userId}`);
          setSuccess("User deleted successfully");
          fetchUsers();
        } catch (err) {
          setError(err.response?.data?.error || "Failed to delete user");
        }
      }
    );
  };

  const fetchBackups = () => {};

  const handleExportData = () => {
    try {
      exportDataAsFile();
      setSuccess("Data exported successfully as JSON file");
    } catch (err) {
      setError("Failed to export data");
    }
  };

  const handleImportData = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    showConfirmation(
      "Import Data",
      "This will overwrite all current data. Are you sure you want to proceed?",
      () => {
        const reader = new FileReader();
        reader.onload = (evt) => {
          try {
            const data = JSON.parse(evt.target.result);
            importDataFromObject(data);
            setSuccess("Data imported successfully. Refreshing...");
            setTimeout(() => window.location.reload(), 1000);
          } catch (err) {
            setError("Invalid JSON file");
          }
        };
        reader.readAsText(file);
      },
      () => {
        e.target.value = "";
      }
    );
  };

  const handleResetTransactions = () => {
    showConfirmation(
      "Clear All Transactions",
      "Delete ALL transactions? Your inventory will be preserved. This action cannot be undone.",
      async () => {
        try {
          await resetTransactions();
          setSuccess("All transactions cleared. Inventory preserved.");
          fetchStatistics();
        } catch (err) {
          setError("Failed to reset transactions");
        }
      }
    );
  };

  const fetchItems = async () => {
    try {
      const response = await api.get("/items");
      setItems(response.data);
    } catch (err) {
      setError("Failed to fetch items");
    }
  };

  const fetchTransactions = async () => {
    setTransactionsLoading(true);
    try {
      const response = await api.get("/transactions");
      setTransactions(response.data || []);
    } catch (err) {
      setError("Failed to fetch transactions");
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchTransactionDetails = async (transactionId) => {
    try {
      const response = await api.get(`/transactions/${transactionId}`);
      setSelectedTransaction(response.data);
      setShowTransactionModal(true);
    } catch (err) {
      setError("Failed to fetch transaction details");
    }
  };

  const handleBarcodeScan = (e) => {
    const barcode = e.target.value.trim();
    if (barcode && e.key === "Enter") {
      setFormData({ ...formData, barcode });
      e.target.value = "";
      // Focus on name input
      setTimeout(() => {
        document.querySelector('input[name="name"]')?.focus();
      }, 100);
    }
  };

  const handleCameraScan = (barcode) => {
    if (barcode && barcode.trim()) {
      setFormData({ ...formData, barcode: barcode.trim() });
      // Focus on name input after scanning
      setTimeout(() => {
        document.querySelector('input[name="name"]')?.focus();
      }, 100);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const nextValue = type === "checkbox" ? checked : value;
    setFormData((current) => ({ ...current, [name]: nextValue }));
    if (name === "price") {
      setProductUnits((current) => current.map((unit) =>
        unit.is_base_unit ? { ...unit, selling_price: value } : unit));
    }
  };

  const handleProductUnitsChange = (nextUnits) => {
    setProductUnits(nextUnits);
    const baseUnit = nextUnits.find(
      (unit) => unit.is_base_unit && unit.is_active !== false,
    );
    if (baseUnit) {
      setFormData((current) => ({
        ...current,
        price: String(baseUnit.selling_price ?? ""),
      }));
    }
  };

  const saveItemWithUnits = async (normalizedUnits) => {
    setLoading(true);
    try {
      const itemPayload = {
        ...formData,
        barcode: getParentBarcodeForSave(editingItem, formData.barcode),
        units: normalizedUnits,
      };
      if (editingItem) {
        await api.put(`/items/${editingItem.id}`, itemPayload);
        setSuccess("Item updated successfully");
      } else {
        await api.post("/items", itemPayload);
        setSuccess("Item added successfully");
      }
      setFormData({ barcode: "", name: "", price: "", base_price: "", category: "", stock: "" });
      setProductUnits(defaultProductUnits());
      setShowProductUnitValidation(false);
      setEditingItem(null);
      setShowModal(false);
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to save item");
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitPeriod = async (nextProfitPeriod) => {
    setProfitLoading(true);
    try {
      const response = await api.get("/statistics/profit", {
        profitPeriod: nextProfitPeriod,
      });
      setStatistics((current) => current
        ? { ...current, profit: response.data }
        : current);
      setProfitPeriod(nextProfitPeriod);
    } catch (err) {
      setError("Failed to fetch profit for this period");
    } finally {
      setProfitLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setShowProductUnitValidation(true);

    const unitErrors = validateProductUnits(productUnits);
    if (Object.keys(unitErrors).length) {
      setError("Correct the Packaging Units errors before saving.");
      return;
    }

    const normalizedUnits = normalizeProductUnits(productUnits);
    const conflicts = findSharedUnitBarcodeConflicts(
      normalizedUnits,
      items,
      editingItem?.id,
    );

    if (conflicts.length) {
      const sharedBarcodes = conflicts
        .map((conflict) => conflict.barcode)
        .join(", ");
      const matchingUnits = conflicts.reduce(
        (total, conflict) => total + conflict.matchCount,
        0,
      );
      showConfirmation(
        "Share packaging barcode?",
        `${sharedBarcodes} already matches ${matchingUnits} packaging unit${matchingUnits === 1 ? "" : "s"}. Share ${conflicts.length === 1 ? "this barcode" : "these barcodes"}?`,
        () => saveItemWithUnits(
          approveSharedUnitBarcodes(normalizedUnits, conflicts),
        ),
      );
      return;
    }

    saveItemWithUnits(normalizedUnits);
  };

  const handleEdit = async (item) => {
    setEditingItem(item);
    setFormData({
      barcode: item.barcode,
      name: item.name,
      price: item.price,
      base_price: item.base_price || "",
      category: item.category || "",
      stock: item.stock,
    });
    setProductUnits(defaultProductUnits(item.price));
    setShowProductUnitValidation(false);
    setShowCameraFallback(false);
    setShowModal(true);
    setError("");
    setSuccess("");
    try {
      const existingUnits = await getProductUnits(item.id);
      setProductUnits(
        existingUnits.length ? existingUnits : defaultProductUnits(item.price),
      );
    } catch {
      setProductUnits(defaultProductUnits(item.price));
      setError("Item loaded, but its Packaging Units could not be loaded.");
    }
  };

  const handleDelete = (id) => {
    showConfirmation(
      "Delete Item",
      "Permanently delete this unsold item and its packaging units? This action cannot be undone.",
      async () => {
        try {
          await api.delete(`/items/${id}`);
          setSuccess("Item deleted successfully");
          fetchItems();
        } catch (err) {
          setError(err.response?.data?.error || "Failed to delete item");
        }
      }
    );
  };

  const handleViewItemDetails = async (item) => {
    setSelectedItem(item);
    setDetailsUnits(item.product_units || []);
    setShowItemDetailsModal(true);
    try {
      const units = await getProductUnits(item.id);
      const knownCounts = new Map(
        (item.product_units || []).map((unit) => [
          String(unit.id),
          unit.shared_barcode_count,
        ]),
      );
      setDetailsUnits(units.map((unit) => ({
        ...unit,
        shared_barcode_count: knownCounts.get(String(unit.id)) || 0,
      })));
    } catch {
      setDetailsUnits(item.product_units || []);
    }
  };

  const handleLogout = async () => {
    await authLogout();
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({
      barcode: "",
      name: "",
      price: "",
      base_price: "",
      category: "",
      stock: "",
    });
    setProductUnits(defaultProductUnits());
    setShowProductUnitValidation(false);
    setShowCameraFallback(false);
    setShowModal(true);
    setError("");
    setSuccess("");
    setTimeout(() => {
      barcodeInputRef.current?.focus();
    }, 100);
  };

  const handleSaveCategory = async () => {
    if (!categoryFormData.name.trim()) {
      setError("Category name is required");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.id}`, categoryFormData);
        setSuccess("Category updated successfully");
      } else {
        await api.post("/categories", categoryFormData);
        setSuccess("Category created successfully");
      }
      setCategoryFormData({ name: "", description: "" });
      setEditingCategory(null);
      setShowCategoryModal(false);
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save category");
    } finally {
      setLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryFormData({
      name: category.name || "",
      description: category.description || "",
    });
    setShowCategoryModal(true);
    setError("");
    setSuccess("");
  };

  const handleDeleteCategory = (categoryId) => {
    showConfirmation(
      "Delete Category",
      "Are you sure you want to delete this category? This action cannot be undone.",
      async () => {
        try {
          await api.delete(`/categories/${categoryId}`);
          setSuccess("Category deleted successfully");
          if (editingCategory?.id === categoryId) {
            setEditingCategory(null);
            setCategoryFormData({ name: "", description: "" });
            setShowCategoryModal(false);
          }
          fetchCategories();
        } catch (err) {
          setError(err.response?.data?.error || "Failed to delete category");
        }
      }
    );
  };

  const handleQuickAddCategory = async () => {
    if (!newCategoryInput.trim()) {
      return;
    }

    try {
      await api.post("/categories", { name: newCategoryInput.trim() });
      await fetchCategories();
      setFormData({ ...formData, category: newCategoryInput.trim() });
      setNewCategoryInput("");
      setSuccess("Category added and selected");
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create category");
    }
  };

  // ── Filter + pagination computed BEFORE return so React always re-evaluates on state change ──

  // Low-stock dashboard
  const rawLowStockItems = (statistics && statistics.lowStockItems) || [];
  const filteredLowStock = rawLowStockItems.filter((item) => {
    const matchesSearch =
      !lowStockFilters.search ||
      (item.name || "").toLowerCase().includes(lowStockFilters.search.toLowerCase()) ||
      (item.barcode || "").toLowerCase().includes(lowStockFilters.search.toLowerCase());
    const matchesCategory =
      !lowStockFilters.category || item.category === lowStockFilters.category;
    const matchesStockLevel =
      lowStockFilters.stockLevel === "all" ||
      (lowStockFilters.stockLevel === "out" && item.stock === 0) ||
      (lowStockFilters.stockLevel === "low" && item.stock > 0 && item.stock <= 10);
    return matchesSearch && matchesCategory && matchesStockLevel;
  });
  const totalLowStockPages = Math.ceil(filteredLowStock.length / itemsPerPage);
  const paginatedLowStock = filteredLowStock.slice(
    (lowStockPage - 1) * itemsPerPage,
    lowStockPage * itemsPerPage,
  );

  // Inventory
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      inventoryItemMatchesSearch(item, inventoryFilters.search);
    const matchesCategory =
      !inventoryFilters.category || item.category === inventoryFilters.category;
    const matchesStockLevel =
      inventoryFilters.stockLevel === "all" ||
      (inventoryFilters.stockLevel === "in_stock" && item.stock > 10) ||
      (inventoryFilters.stockLevel === "low" && item.stock > 0 && item.stock <= 10) ||
      (inventoryFilters.stockLevel === "out" && item.stock === 0);
    return matchesSearch && matchesCategory && matchesStockLevel;
  }).sort((a, b) => {
    if (inventorySort === "name") {
      return a.name.localeCompare(b.name);
    } else if (inventorySort === "created-asc") {
      return new Date(a.created_at) - new Date(b.created_at);
    } else if (inventorySort === "created-desc") {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return 0;
  });
  const totalInventoryPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (inventoryPage - 1) * itemsPerPage,
    inventoryPage * itemsPerPage,
  );

  // Transactions
  const filteredTransactions = filterReportTransactions(
    transactions,
    transactionFilters,
  );
  const reportSummary = calculateReportSummary(filteredTransactions);
  const totalTransactionPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const showingAllTransactions =
    !transactionFilters.search &&
    !transactionFilters.dateFrom &&
    !transactionFilters.dateTo;
  const visibleTransactions = getVisibleReportTransactions(
    filteredTransactions,
    transactionPage,
    itemsPerPage,
  );

  const openingStockUnitName = getBaseUnitDisplayName(productUnits);
  const showParentBarcodeField = shouldRequireParentBarcode(editingItem);

  return (
    <div className="app-layout">
      <Sidebar
        user={user}
        onLogout={handleLogout}
        currentView={currentView}
        onViewChange={setCurrentView}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
        menuButtonRef={mobileMenuButtonRef}
      />
      <main className={`main-content ${sidebarCollapsed ? "sidebar-collapsed" : ""}`}>
        {/* Mobile Menu Button — hidden when sidebar overlay is open */}
        <button
          ref={mobileMenuButtonRef}
          className="mobile-menu-btn"
          onClick={handleOpenMenu}
          aria-label="Open menu"
          aria-controls="app-sidebar"
          aria-expanded={sidebarOpen}
          style={{ display: sidebarOpen ? "none" : undefined }}
        >
          <span></span>
        </button>

        <WorkspaceHeader
          workspaceLabel="Admin"
          pageLabel={ADMIN_PAGE_LABELS[currentView] || "Dashboard"}
          stationLabel="Back office"
          user={user}
          onOpenCashier={() => navigate("/cashier")}
        />

        {currentView === "purchases" && user?.role === "admin" && (
          <div className="card purchase-workspace-card">
            <PurchasesView
              categories={categories}
              onInventoryChanged={fetchItems}
            />
          </div>
        )}

        {currentView === "suppliers" && user?.role === "admin" && (
          <div className="card purchase-workspace-card">
            <SupplierManager mode="page" />
          </div>
        )}

        {/* Dashboard View */}
        {currentView === "dashboard" && user?.role === "admin" && (
          <div className="card dashboard-page">
            <header className="dashboard-page-header">
              <div>
                <h2>Dashboard &amp; Statistics</h2>
                <p className="dashboard-page-subtitle">
                  Monitor today&apos;s sales and act on inventory risks.
                </p>
              </div>
              <button
                type="button"
                className="btn btn-secondary dashboard-refresh-button"
                onClick={fetchStatistics}
                disabled={statsLoading}
                aria-label="Refresh dashboard statistics"
              >
                {statsLoading ? "Refreshing..." : "Refresh"}
              </button>
            </header>

            {statsLoading ? (
              <div className="empty-state dashboard-loading-state">
                <p>Loading statistics...</p>
              </div>
            ) : statistics ? (
              <>
                {/* Stock Overview */}
                <section
                  className="dashboard-stock-section"
                  aria-labelledby="dashboard-stock-title"
                >
                  <div className="dashboard-section-heading">
                    <div>
                      <h3 id="dashboard-stock-title">Stock health</h3>
                      <p>Current availability across the product catalog.</p>
                    </div>
                  </div>
                  <div className="dashboard-stock-grid">
                    {[
                      {
                        label: "Total Items",
                        value: statistics.stock?.total_items || 0,
                        meta: "Catalog size",
                        toneClass: "is-total",
                      },
                      {
                        label: "In Stock",
                        value: statistics.stock?.in_stock || 0,
                        meta: "Available for sale",
                        toneClass: "is-success",
                      },
                      {
                        label: "Out of Stock",
                        value: statistics.stock?.out_of_stock || 0,
                        meta: "Requires restocking",
                        toneClass: "is-danger",
                      },
                      {
                        label: "Low Stock",
                        value: statistics.stock?.low_stock || 0,
                        meta: "10 units or fewer",
                        toneClass: "is-warning",
                      },
                    ].map((stat) => (
                      <article
                        key={stat.label}
                        className={`dashboard-stat-card ${stat.toneClass}`}
                      >
                        <div className="dashboard-stat-label">
                          <span className="dashboard-stat-dot" aria-hidden="true" />
                          {stat.label}
                        </div>
                        <div className="dashboard-stat-value">{stat.value}</div>
                        <div className="dashboard-stat-meta">{stat.meta}</div>
                      </article>
                    ))}
                  </div>
                </section>

                {/* Today's Sales */}
                <section
                  className="dashboard-sales-section"
                  aria-labelledby="dashboard-sales-title"
                >
                  <div className="dashboard-section-heading">
                    <div>
                      <h3 id="dashboard-sales-title">Today&apos;s sales</h3>
                      <p>Sales performance recorded for the current day.</p>
                    </div>
                  </div>
                  <div className="dashboard-sales-grid">
                    <article className="dashboard-sales-metric is-featured">
                      <span className="dashboard-sales-label">Total Revenue</span>
                      <span className="dashboard-sales-value">
                        MMK {formatCurrency(statistics.today?.total_revenue || 0)}
                      </span>
                      <span className="dashboard-sales-meta">Gross sales today</span>
                    </article>
                    <article className="dashboard-sales-metric">
                      <span className="dashboard-sales-label">Transactions</span>
                      <span className="dashboard-sales-value">
                        {statistics.today?.transaction_count || 0}
                      </span>
                    </article>
                    <article className="dashboard-sales-metric">
                      <span className="dashboard-sales-label">Avg Transaction</span>
                      <span className="dashboard-sales-value">
                        MMK {formatCurrency(statistics.today?.avg_transaction || 0)}
                      </span>
                    </article>
                    <article className="dashboard-sales-metric is-positive">
                      <span className="dashboard-sales-label">Today&apos;s Profit</span>
                      <span className="dashboard-sales-value">
                        MMK {formatCurrency(statistics.today?.total_profit || 0)}
                      </span>
                    </article>
                  </div>
                  {statistics.profit && (
                    <div className="dashboard-profit-selector">
                      <div
                        className="dashboard-profit-periods"
                        role="group"
                        aria-label="Profit period"
                        aria-busy={profitLoading}
                      >
                        {PROFIT_PERIOD_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`btn btn-sm ${profitPeriod === option.value ? "btn-primary" : "btn-secondary"}`}
                            aria-pressed={profitPeriod === option.value}
                            disabled={profitLoading}
                            onClick={() => fetchProfitPeriod(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                      <div className="dashboard-profit-summary">
                      <div>
                        <span className="dashboard-sales-label">
                          {PROFIT_PERIOD_OPTIONS.find((option) => option.value === statistics.profit.period)?.label || "Profit"}
                        </span>
                        <span className="dashboard-profit-summary-note">
                          {profitLoading
                            ? "Updating profit…"
                            : `${formatDashboardDate(statistics.profit.start)} – ${formatDashboardDate(statistics.profit.end)}`}
                        </span>
                      </div>
                      <strong>
                        MMK {formatCurrency(statistics.profit.total_profit || 0)}
                      </strong>
                    </div>
                    </div>
                  )}
                </section>

                {/* Low Stock Items */}
                {rawLowStockItems.length > 0 && (
                      <section
                        className="dashboard-low-stock-section"
                        aria-labelledby="dashboard-low-stock-title"
                      >
                        <div className="dashboard-section-heading dashboard-low-stock-heading">
                          <div>
                            <h3 id="dashboard-low-stock-title">Restock queue</h3>
                            <p>Items at 10 units or below that need attention.</p>
                          </div>
                          <div className="dashboard-low-stock-summary">
                            <span className="badge badge-warning">Need refill</span>
                            <span className="pagination-info">
                              Showing {paginatedLowStock.length} of {filteredLowStock.length} items
                            </span>
                          </div>
                        </div>

                        {/* Filters */}
                        <div className="filter-row dashboard-low-stock-filters">
                          <div className="search-input-wrap">
                            <input
                              type="text"
                              aria-label="Search low stock items"
                              placeholder="Search by name or barcode..."
                              value={lowStockFilters.search}
                              onChange={(e) => {
                                const v = e.target.value;
                                setLowStockFilters((prev) => ({ ...prev, search: v }));
                                setLowStockPage(1);
                              }}
                            />
                            {lowStockFilters.search && (
                              <button
                                type="button"
                                className="search-clear-btn"
                                onClick={() => { setLowStockFilters((prev) => ({ ...prev, search: "" })); setLowStockPage(1); }}
                                title="Clear search"
                                aria-label="Clear low stock search"
                              >×</button>
                            )}
                          </div>
                          <select
                            aria-label="Filter low stock items by category"
                            value={lowStockFilters.category}
                            onChange={(e) => {
                              const v = e.target.value;
                              setLowStockFilters((prev) => ({ ...prev, category: v }));
                              setLowStockPage(1);
                            }}
                          >
                            <option value="">All Categories</option>
                            {categories.map((cat) => (
                              <option key={cat.id} value={cat.name}>
                                {cat.name}
                              </option>
                            ))}
                          </select>
                          <select
                            aria-label="Filter low stock items by stock level"
                            value={lowStockFilters.stockLevel}
                            onChange={(e) => {
                              const v = e.target.value;
                              setLowStockFilters((prev) => ({ ...prev, stockLevel: v }));
                              setLowStockPage(1);
                            }}
                          >
                            <option value="all">All Stock Levels</option>
                            <option value="out">Out of Stock</option>
                            <option value="low">Low Stock (1-10)</option>
                          </select>
                        </div>

                        {paginatedLowStock.length > 0 ? (
                          <>
                            <div className="table-wrap dashboard-low-stock-table-wrap">
                            <table>
                              <thead>
                                <tr>
                                  <th className="col-name">Item Name</th>
                                  <th className="col-barcode">Barcode</th>
                                  <th className="col-category">Category</th>
                                  <th className="col-price">Price</th>
                                  <th className="col-stock">Stock Left</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paginatedLowStock.map((item) => (
                                  <tr key={item.id}>
                                    <td className="col-name">
                                      <strong>{item.name}</strong>
                                    </td>
                                    <td className="col-barcode">
                                      {item.barcode}
                                    </td>
                                    <td className="col-category">
                                      {item.category || "-"}
                                    </td>
                                    <td className="col-price">
                                      MMK {formatCurrency(item.price)}
                                    </td>
                                    <td className="col-stock">
                                      <span className={`badge ${item.stock === 0 ? "badge-danger" : "badge-warning"}`}>
                                        {item.stock === 0 ? "OUT OF STOCK" : `${item.stock} left`}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            </div>

                            {/* Pagination */}
                            {totalLowStockPages > 1 && (
                              <div className="pagination-controls dashboard-pagination">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setLowStockPage(
                                      Math.max(1, lowStockPage - 1),
                                    )
                                  }
                                  disabled={lowStockPage === 1}
                                  className="btn btn-secondary"
                                >
                                  Previous
                                </button>
                                <div className="dashboard-pagination-status">
                                  <span>Page</span>
                                  <input
                                    key={`lowstock-input-${lowStockPage}`}
                                    type="number"
                                    aria-label="Low stock results page"
                                    min="1"
                                    max={totalLowStockPages}
                                    defaultValue={lowStockPage}
                                    onBlur={(e) => {
                                      const value = e.target.value.trim();
                                      if (value === "") {
                                        e.target.value = lowStockPage;
                                        return;
                                      }
                                      const page = parseInt(value);
                                      if (isNaN(page) || page < 1) {
                                        e.target.value = 1;
                                        setLowStockPage(1);
                                      } else if (page > totalLowStockPages) {
                                        e.target.value = totalLowStockPages;
                                        setLowStockPage(totalLowStockPages);
                                      } else {
                                        e.target.value = page;
                                        setLowStockPage(page);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        const value = e.target.value.trim();
                                        if (value === "") {
                                          e.target.value = lowStockPage;
                                          return;
                                        }
                                        const page = parseInt(value);
                                        if (isNaN(page) || page < 1) {
                                          e.target.value = 1;
                                          setLowStockPage(1);
                                        } else if (page > totalLowStockPages) {
                                          e.target.value = totalLowStockPages;
                                          setLowStockPage(totalLowStockPages);
                                        } else {
                                          e.target.value = page;
                                          setLowStockPage(page);
                                        }
                                        e.target.blur();
                                      }
                                    }}
                                  />
                                  <span>of {totalLowStockPages}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setLowStockPage(
                                      Math.min(totalLowStockPages, lowStockPage + 1),
                                    )
                                  }
                                  disabled={lowStockPage === totalLowStockPages}
                                  className="btn btn-secondary"
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="dashboard-filter-empty">
                            No items match the current filters
                          </div>
                        )}
                      </section>
                )}
              </>
            ) : (
              <div className="empty-state">
                <p>No statistics available</p>
              </div>
            )}
          </div>
        )}

        {/* Inventory View */}
        {currentView === "inventory" && (
          <div className="card">
            <div className="section-header">
              <div>
                <h2>Inventory Management</h2>
                <p style={{ margin: 0 }}>{items.length} products</p>
              </div>
              <button className="btn btn-primary" onClick={handleAddNew}>
                + Add Product
              </button>
            </div>

            {/* Filters */}
            {items.length > 0 && (
              <div className="filter-row">
                <div className="search-input-wrap">
                  <input
                    type="text"
                    placeholder="Search by name or barcode..."
                    value={inventoryFilters.search}
                    onChange={(e) => {
                      const v = e.target.value;
                      setInventoryFilters((prev) => ({ ...prev, search: v }));
                      setInventoryPage(1);
                    }}
                  />
                  {inventoryFilters.search && (
                    <button
                      className="search-clear-btn"
                      onClick={() => { setInventoryFilters((prev) => ({ ...prev, search: "" })); setInventoryPage(1); }}
                      title="Clear search"
                    >×</button>
                  )}
                </div>
                <select
                  value={inventorySort}
                  onChange={(e) => {
                    setInventorySort(e.target.value);
                    setInventoryPage(1);
                  }}
                  title="Sort items"
                >
                  <option value="name">Sort by Name</option>
                  <option value="created-asc">Oldest First</option>
                  <option value="created-desc">Newest First</option>
                </select>
                <select
                  value={inventoryFilters.category}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInventoryFilters((prev) => ({ ...prev, category: v }));
                    setInventoryPage(1);
                  }}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                <select
                  value={inventoryFilters.stockLevel}
                  onChange={(e) => {
                    const v = e.target.value;
                    setInventoryFilters((prev) => ({ ...prev, stockLevel: v }));
                    setInventoryPage(1);
                  }}
                >
                  <option value="all">All Stock Levels</option>
                  <option value="in_stock">In Stock</option>
                  <option value="low">Low Stock (1-10)</option>
                  <option value="out">Out of Stock</option>
                </select>
              </div>
            )}

            {items.length === 0 ? (
              <div className="empty-state">
                <p>No items found. Add your first item to get started.</p>
              </div>
            ) : (
              <>
                    {filteredItems.length > 0 ? (
                      <>
                        <div className="d-flex justify-between align-center mb-4">
                          <span className="pagination-info">
                            Showing {paginatedItems.length} of {filteredItems.length} items
                          </span>
                        </div>
                        {/* ── Desktop / Tablet Table ── */}
                        <div className="table-wrap inventory-table-wrap">
                        <table className="inventory-table">
                          <thead>
                            <tr>
                              <th className="col-barcode">Barcode</th>
                              <th className="col-name">Name</th>
                              <th className="col-price">Sell Price</th>
                              <th className="col-base-price">Base Price</th>
                              <th className="col-profit">Profit/Unit</th>
                              <th className="col-category">Category</th>
                              <th className="col-stock">Stock</th>
                              <th className="col-actions">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedItems.map((item) => {
                              const basePrice = item.base_price || 0;
                              const profit = parseFloat(item.price) - basePrice;
                              const barcodeDisplay = getInventoryBarcodeDisplay(
                                item,
                                inventoryFilters.search,
                              );
                              const profitPercent =
                                item.price > 0
                                  ? (profit / parseFloat(item.price)) * 100
                                  : 0;
                              return (
                                <tr key={item.id}>
                                  <td className="col-barcode">
                                    <span>{barcodeDisplay.primary}</span>
                                    {barcodeDisplay.additionalCount > 0 && (
                                      <small className="inventory-unit-count">
                                        +{barcodeDisplay.additionalCount} unit barcode{barcodeDisplay.additionalCount === 1 ? "" : "s"}
                                      </small>
                                    )}
                                  </td>
                                  <td className="col-name" style={{ cursor: "pointer" }} onClick={() => handleViewItemDetails(item)} title="Click to view details">{item.name}</td>
                                  <td className="col-price">MMK {formatCurrency(item.price)}</td>
                                  <td className="col-base-price">{basePrice > 0 ? `MMK ${formatCurrency(basePrice)}` : "-"}</td>
                                  <td className="col-profit">
                                    {basePrice > 0 ? (
                                      <span className={profit >= 0 ? "text-profit" : "text-loss"}>
                                        MMK {formatCurrency(profit)} ({profitPercent.toFixed(1)}%)
                                      </span>
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td className="col-category">{item.category || "-"}</td>
                                  <td className="col-stock">{item.stock}</td>
                                  <td className="col-actions">
                                    <div className="tbl-actions">
                                      <button
                                        type="button"
                                        className="table-row-action table-row-action-details btn-details-only"
                                        onClick={() => handleViewItemDetails(item)}
                                      >
                                        Details
                                      </button>
                                      <button
                                        type="button"
                                        className="table-row-action table-row-action-edit"
                                        onClick={() => handleEdit(item)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        className="table-row-action table-row-action-delete"
                                        onClick={() => handleDelete(item.id)}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                    <div className="inventory-actions-mobile" data-inventory-item-menu={item.id}>
                                      <button type="button" className="inventory-actions-menu-btn" aria-haspopup="menu" aria-expanded={openItemMenuId === item.id} onClick={(e) => { e.stopPropagation(); setOpenItemMenuId((prev) => prev === item.id ? null : item.id); }} title="Menu">⋮</button>
                                      {openItemMenuId === item.id && (
                                        <div className="inventory-actions-dropdown" role="menu">
                                          <button type="button" className="inventory-actions-dropdown-item" onClick={() => { setOpenItemMenuId(null); handleViewItemDetails(item); }}>View details</button>
                                          <button type="button" className="inventory-actions-dropdown-item" onClick={() => { setOpenItemMenuId(null); handleEdit(item); }}>Edit</button>
                                          <button type="button" className="inventory-actions-dropdown-item danger" onClick={() => { setOpenItemMenuId(null); handleDelete(item.id); }}>Delete</button>
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                        </div>

                        {/* ── Mobile Card List (≤ 640px) ── */}
                        <div className="mobile-inv-cards">
                          {paginatedItems.map((item) => {
                            const basePrice = item.base_price || 0;
                            const profit = parseFloat(item.price) - basePrice;
                            const profitPct = item.price > 0 ? (profit / parseFloat(item.price)) * 100 : 0;
                            const barcodeDisplay = getInventoryBarcodeDisplay(
                              item,
                              inventoryFilters.search,
                            );
                            return (
                              <div key={`card-${item.id}`} className="mobile-inv-card">
                                <button
                                  type="button"
                                  className="mic-body mic-body-button"
                                  onClick={() => handleViewItemDetails(item)}
                                  aria-label={`View details for ${item.name}`}
                                >
                                  <div className="mic-name">{item.name}</div>
                                  <div className="mic-barcode">
                                    {barcodeDisplay.primary}
                                    {barcodeDisplay.additionalCount > 0 && (
                                      <span> · +{barcodeDisplay.additionalCount} units</span>
                                    )}
                                  </div>
                                  <div className="mic-meta">
                                    <span className="mic-price">MMK {formatCurrency(item.price)}</span>
                                    {item.category && <span className="mic-cat">{item.category}</span>}
                                    <span className={`mic-stock ${item.stock === 0 ? "mic-stock-out" : item.stock <= 10 ? "mic-stock-low" : ""}`}>
                                      {item.stock === 0 ? "Out of stock" : `${item.stock} pcs`}
                                    </span>
                                    {basePrice > 0 && (
                                      <span className={`mic-profit ${profit >= 0 ? "text-profit" : "text-loss"}`}>
                                        {profit >= 0 ? "+" : ""}{profitPct.toFixed(0)}% margin
                                      </span>
                                    )}
                                  </div>
                                </button>
                                <div className="mic-actions" data-inventory-item-menu={`card-${item.id}`} onClick={(e) => e.stopPropagation()}>
                                  <button
                                    type="button"
                                    className="inventory-actions-menu-btn"
                                    aria-haspopup="menu"
                                    aria-expanded={openItemMenuId === `card-${item.id}`}
                                    onClick={(e) => { e.stopPropagation(); setOpenItemMenuId((prev) => prev === `card-${item.id}` ? null : `card-${item.id}`); }}
                                  >⋮</button>
                                  {openItemMenuId === `card-${item.id}` && (
                                    <div className="inventory-actions-dropdown" role="menu">
                                      <button type="button" className="inventory-actions-dropdown-item" onClick={() => { setOpenItemMenuId(null); handleViewItemDetails(item); }}>View details</button>
                                      <button type="button" className="inventory-actions-dropdown-item" onClick={() => { setOpenItemMenuId(null); handleEdit(item); }}>Edit</button>
                                      <button type="button" className="inventory-actions-dropdown-item danger" onClick={() => { setOpenItemMenuId(null); handleDelete(item.id); }}>Delete</button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Pagination */}
                        {totalInventoryPages > 1 && (
                          <div className="pagination-controls">
                            <button
                              onClick={() => setInventoryPage(Math.max(1, inventoryPage - 1))}
                              disabled={inventoryPage === 1}
                              className="btn btn-secondary btn-sm"
                            >
                              Previous
                            </button>
                            <div className="d-flex align-center gap-2">
                              <span className="pagination-info">Page</span>
                              <input
                                key={`inventory-page-${inventoryPage}`}
                                type="number"
                                min="1"
                                max={totalInventoryPages}
                                defaultValue={inventoryPage}
                                onBlur={(e) => {
                                  const page = parseInt(e.target.value);
                                  if (!isNaN(page) && page >= 1 && page <= totalInventoryPages) {
                                    setInventoryPage(page);
                                  } else {
                                    e.target.value = inventoryPage;
                                  }
                                }}
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    const page = parseInt(e.target.value);
                                    if (!isNaN(page) && page >= 1 && page <= totalInventoryPages) {
                                      setInventoryPage(page);
                                    } else {
                                      e.target.value = inventoryPage;
                                    }
                                    e.target.blur();
                                  }
                                }}
                                style={{ width: "60px", textAlign: "center" }}
                              />
                              <span className="pagination-info">of {totalInventoryPages}</span>
                            </div>
                            <button
                              onClick={() => setInventoryPage(Math.min(totalInventoryPages, inventoryPage + 1))}
                              disabled={inventoryPage === totalInventoryPages}
                              className="btn btn-secondary btn-sm"
                            >
                              Next
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "var(--text-secondary)",
                          background: "var(--bg-secondary)",
                          borderRadius: "8px",
                        }}
                      >
                        No items match the current filters
                      </div>
                    )}
              </>
            )}
          </div>
        )}

        {/* Category Management - Show in inventory view */}
        {currentView === "inventory" && user?.role === "admin" && (
          <div className="card">
            <div
              className="section-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2>Category Management</h2>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setEditingCategory(null);
                  setCategoryFormData({ name: "", description: "" });
                  setShowCategoryModal(true);
                  setError("");
                  setSuccess("");
                }}
              >
                Add New Category
              </button>
            </div>

            {categories.length === 0 ? (
              <div className="empty-state">
                <p>No categories found. Create your first category.</p>
              </div>
            ) : (
              <div className="table-wrap category-table-wrap">
              <table className="category-table">
                <thead>
                  <tr>
                    <th>Category Name</th>
                    <th className="col-description">Description</th>
                    <th className="col-created">Created</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat) => (
                    <tr key={cat.id}>
                      <td>
                        <strong>{cat.name}</strong>
                      </td>
                      <td className="col-description">{cat.description || "-"}</td>
                      <td className="col-created">
                        {new Date(cat.created_at).toLocaleDateString()}
                      </td>
                      <td className="col-actions">
                        <div className="category-actions-desktop">
                          <button
                            type="button"
                            className="table-row-action table-row-action-edit"
                            onClick={() => handleEditCategory(cat)}
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="table-row-action table-row-action-delete"
                            onClick={() => handleDeleteCategory(cat.id)}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>

                        <div
                          className="category-actions-mobile"
                          data-category-menu={cat.id}
                        >
                          <button
                            type="button"
                            className="inventory-actions-menu-btn"
                            aria-haspopup="menu"
                            aria-expanded={openCategoryMenuId === cat.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenCategoryMenuId((prev) =>
                                prev === cat.id ? null : cat.id,
                              );
                            }}
                            title="Menu"
                          >
                            ⋮
                          </button>
                          {openCategoryMenuId === cat.id && (
                            <div
                              className="inventory-actions-dropdown"
                              role="menu"
                            >
                              <button
                                type="button"
                                className="inventory-actions-dropdown-item"
                                onClick={() => {
                                  setOpenCategoryMenuId(null);
                                  handleEditCategory(cat);
                                }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="inventory-actions-dropdown-item danger"
                                onClick={() => {
                                  setOpenCategoryMenuId(null);
                                  handleDeleteCategory(cat.id);
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {/* User Management - Show in inventory view */}
        {currentView === "inventory" && user?.role === "admin" && (
          <div className="card">
            <div
              className="section-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2>User Management</h2>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setUserFormData({
                    username: "",
                    password: "",
                    role: "cashier",
                    fullName: "",
                  });
                  setShowUserModal(true);
                  setError("");
                  setSuccess("");
                }}
              >
                Register New User
              </button>
            </div>

            {users.length === 0 ? (
              <div className="empty-state">
                <p>No users found.</p>
              </div>
            ) : (
              <div className="table-wrap users-table-wrap"><table className="users-table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Full Name</th>
                    <th>Role</th>
                    <th className="col-created">Created</th>
                    <th className="col-actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      <td>{u.username}</td>
                      <td>{u.full_name || "-"}</td>
                      <td>
                        <span className={`badge ${u.role === "admin" ? "badge-info" : "badge-neutral"}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="col-created">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="col-actions">
                        {u.id !== user?.id && (
                          <button
                            className="btn btn-danger"
                            onClick={() => handleDeleteUser(u.id)}
                            title="Delete user"
                            style={{ padding: "6px 12px", fontSize: "14px" }}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        )}

        {/* Reports View */}
        {currentView === "reports" && user?.role === "admin" && (
          <div className="card reports-page">
            <header className="reports-header">
              <div>
                <p className="reports-eyebrow">Sales performance</p>
                <h2>Sales Reports</h2>
                <p className="reports-subtitle">
                  Review revenue, filter transactions, and inspect every sale.
                </p>
              </div>
              <span className="reports-match-count" aria-live="polite">
                {reportSummary.transactionCount} matching{" "}
                {reportSummary.transactionCount === 1
                  ? "transaction"
                  : "transactions"}
              </span>
            </header>

            <section
              className="reports-summary-grid"
              aria-label="Filtered sales summary"
            >
              <article className="reports-summary-card">
                <span className="reports-summary-label">Transactions</span>
                <strong className="reports-summary-value">
                  {reportSummary.transactionCount}
                </strong>
                <span className="reports-summary-meta">Completed sales</span>
              </article>
              <article className="reports-summary-card is-revenue">
                <span className="reports-summary-label">Revenue</span>
                <strong className="reports-summary-value">
                  MMK {formatCurrency(reportSummary.revenue)}
                </strong>
                <span className="reports-summary-meta">
                  Across matching records
                </span>
              </article>
              <article className="reports-summary-card">
                <span className="reports-summary-label">Average Sale</span>
                <strong className="reports-summary-value">
                  MMK {formatCurrency(reportSummary.averageSale)}
                </strong>
                <span className="reports-summary-meta">
                  Revenue per transaction
                </span>
              </article>
              <article className="reports-summary-card">
                <span className="reports-summary-label">Items Sold</span>
                <strong className="reports-summary-value">
                  {formatCurrency(reportSummary.itemsSold)}
                </strong>
                <span className="reports-summary-meta">
                  Units in matching sales
                </span>
              </article>
            </section>

            <section
              className="reports-filter-panel"
              aria-labelledby="reports-filter-title"
            >
              <div className="reports-filter-heading">
                <div>
                  <h3 id="reports-filter-title">Filter transactions</h3>
                  <p>Search by receipt ID or narrow the reporting period.</p>
                </div>
                {(transactionFilters.search ||
                  transactionFilters.dateFrom ||
                  transactionFilters.dateTo) && (
                  <button
                    type="button"
                    className="btn btn-secondary reports-clear-filters"
                    onClick={() => {
                      setTransactionFilters({
                        search: "",
                        dateFrom: "",
                        dateTo: "",
                      });
                      setTransactionPage(1);
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>
              <div className="reports-filter-grid">
                <div className="reports-filter-field reports-search-field">
                  <label htmlFor="reports-transaction-search">
                    Transaction ID
                  </label>
                  <div className="search-input-wrap">
                    <input
                      id="reports-transaction-search"
                      type="search"
                      placeholder="Search transaction ID"
                      value={transactionFilters.search}
                      onChange={(event) => {
                        setTransactionFilters({
                          ...transactionFilters,
                          search: event.target.value,
                        });
                        setTransactionPage(1);
                      }}
                    />
                    {transactionFilters.search && (
                      <button
                        type="button"
                        className="search-clear-btn"
                        onClick={() => {
                          setTransactionFilters({
                            ...transactionFilters,
                            search: "",
                          });
                          setTransactionPage(1);
                        }}
                        aria-label="Clear transaction search"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <div className="reports-filter-field">
                  <label htmlFor="reports-date-from">From date</label>
                  <input
                    id="reports-date-from"
                    type="date"
                    value={transactionFilters.dateFrom}
                    onChange={(event) => {
                      setTransactionFilters({
                        ...transactionFilters,
                        dateFrom: event.target.value,
                      });
                      setTransactionPage(1);
                    }}
                  />
                </div>
                <div className="reports-filter-field">
                  <label htmlFor="reports-date-to">To date</label>
                  <input
                    id="reports-date-to"
                    type="date"
                    value={transactionFilters.dateTo}
                    onChange={(event) => {
                      setTransactionFilters({
                        ...transactionFilters,
                        dateTo: event.target.value,
                      });
                      setTransactionPage(1);
                    }}
                  />
                </div>
              </div>
            </section>

            {transactionsLoading ? (
              <div className="reports-loading-state" role="status">
                Loading transactions...
              </div>
            ) : filteredTransactions.length > 0 ? (
              <section aria-labelledby="reports-results-title">
                <div className="reports-results-heading">
                  <h3 id="reports-results-title">Transactions</h3>
                  <div className="reports-results-count">
                    <span aria-live="polite">
                      Showing {visibleTransactions.length} of{" "}
                      {filteredTransactions.length}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm reports-show-all"
                      aria-pressed={showingAllTransactions}
                      aria-label={
                        showingAllTransactions
                          ? "Show today's transactions"
                          : "Show all transactions"
                      }
                      onClick={() => {
                        setTransactionFilters(
                          applyReportDateScope(
                            transactionFilters,
                            showingAllTransactions ? "today" : "all",
                          ),
                        );
                        setTransactionPage(1);
                      }}
                    >
                      {showingAllTransactions ? "Today" : "All"}
                    </button>
                  </div>
                </div>

                <div className="reports-table-wrap">
                  <table className="reports-table">
                    <thead>
                      <tr>
                        <th>Transaction ID</th>
                        <th>Date &amp; Time</th>
                        <th>Items</th>
                        <th>Total Amount</th>
                        <th className="col-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleTransactions.map((transaction) => (
                        <tr key={transaction.id}>
                          <td>
                            <code>{transaction.transaction_id}</code>
                          </td>
                          <td>
                            {new Date(
                              transaction.created_at,
                            ).toLocaleString()}
                          </td>
                          <td>{transaction.item_count || 0} item(s)</td>
                          <td className="reports-amount">
                            MMK {formatCurrency(transaction.total_amount)}
                          </td>
                          <td className="col-actions">
                            <button
                              type="button"
                              className="table-row-action table-row-action-details"
                              onClick={() =>
                                fetchTransactionDetails(
                                  transaction.transaction_id,
                                )
                              }
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="reports-mobile-list">
                  {visibleTransactions.map((transaction) => (
                    <article
                      className="reports-mobile-card"
                      key={transaction.id}
                    >
                      <div className="reports-mobile-card-header">
                        <code>{transaction.transaction_id}</code>
                        <strong>
                          MMK {formatCurrency(transaction.total_amount)}
                        </strong>
                      </div>
                      <dl className="reports-mobile-meta">
                        <div>
                          <dt>Date &amp; Time</dt>
                          <dd>
                            {new Date(
                              transaction.created_at,
                            ).toLocaleString()}
                          </dd>
                        </div>
                        <div>
                          <dt>Items</dt>
                          <dd>{transaction.item_count || 0} item(s)</dd>
                        </div>
                      </dl>
                      <button
                        type="button"
                        className="table-row-action table-row-action-details"
                        onClick={() =>
                          fetchTransactionDetails(transaction.transaction_id)
                        }
                      >
                        View Details
                      </button>
                    </article>
                  ))}
                </div>

                {totalTransactionPages > 1 && (
                  <nav
                    className="reports-pagination"
                    aria-label="Transaction pages"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setTransactionPage(
                          Math.max(1, transactionPage - 1),
                        )
                      }
                      disabled={transactionPage === 1}
                      className="btn btn-secondary"
                    >
                      Previous
                    </button>
                    <div className="reports-pagination-status">
                      <label htmlFor="reports-page-input">Page</label>
                      <input
                        id="reports-page-input"
                        key={`transaction-page-${transactionPage}`}
                        type="number"
                        min="1"
                        max={totalTransactionPages}
                        defaultValue={transactionPage}
                        onBlur={(event) => {
                          const page = Number.parseInt(
                            event.target.value,
                            10,
                          );
                          if (
                            page >= 1 &&
                            page <= totalTransactionPages
                          ) {
                            setTransactionPage(page);
                          } else {
                            event.target.value = transactionPage;
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter") return;
                          const page = Number.parseInt(
                            event.currentTarget.value,
                            10,
                          );
                          if (
                            page >= 1 &&
                            page <= totalTransactionPages
                          ) {
                            setTransactionPage(page);
                          } else {
                            event.currentTarget.value = transactionPage;
                          }
                          event.currentTarget.blur();
                        }}
                      />
                      <span>of {totalTransactionPages}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setTransactionPage(
                          Math.min(
                            totalTransactionPages,
                            transactionPage + 1,
                          ),
                        )
                      }
                      disabled={
                        transactionPage === totalTransactionPages
                      }
                      className="btn btn-secondary"
                    >
                      Next
                    </button>
                  </nav>
                )}
              </section>
            ) : (
              <div className="reports-empty-state">
                {transactions.length === 0
                  ? "No transactions found. Transactions will appear here after cashiers complete sales."
                  : "No transactions match the current filters."}
              </div>
            )}
          </div>
        )}

        {/* Database Access - Show in inventory view */}
        {currentView === "inventory" && user?.role === "admin" && (
          <div className="card desktop-only">
            <div
              className="section-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h2>Database Access</h2>
            </div>
            <div
              style={{
                padding: "15px",
                background: "var(--bg-secondary)",
                borderRadius: "8px",
                marginBottom: "15px",
              }}
            >
              <p
                style={{ margin: "0 0 10px 0", color: "var(--text-secondary)" }}
              >
                <strong>Database Location:</strong> The SQLite database file is
                stored on the server at:
              </p>
              <code
                style={{
                  display: "block",
                  padding: "10px",
                  background: "var(--bg-primary)",
                  borderRadius: "4px",
                  fontSize: "12px",
                  color: "var(--accent-color)",
                  marginBottom: "15px",
                  wordBreak: "break-all",
                }}
              >
                server/grocery_store.db
              </code>
              <div style={{ marginBottom: "15px" }}>
                <strong>How to Access:</strong>
                <ul
                  style={{
                    margin: "10px 0",
                    paddingLeft: "20px",
                    color: "var(--text-secondary)",
                  }}
                >
                  <li>
                    <strong>Via Admin Panel:</strong> Download the database file
                    directly using the button below
                  </li>
                  <li>
                    <strong>Via SSH:</strong> Connect to your server via SSH and
                    navigate to the project directory
                  </li>
                  <li>
                    <strong>Via FTP/SFTP:</strong> Use FileZilla or similar
                    tools to access the server files
                  </li>
                  <li>
                    <strong>Via Hosting Panel:</strong> Most hosting providers
                    offer file manager access
                  </li>
                </ul>
              </div>
              <button className="btn btn-primary" onClick={handleExportData}>
                📥 Export All Data (JSON)
              </button>
            </div>
          </div>
        )}

        {/* Data Management - Show in inventory view */}
        {currentView === "inventory" && user?.role === "admin" && (
          <div className="card">
            <div className="section-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
              <h2>Data Management</h2>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <div style={{ padding: "15px", background: "var(--bg-secondary)", borderRadius: "8px" }}>
                <strong>Import Backup</strong>
                <p style={{ margin: "8px 0", color: "var(--text-secondary)", fontSize: "14px" }}>
                  Restore from a previously exported JSON backup. <span style={{ color: "var(--warning-color)" }}>⚠️ This overwrites all current data.</span>
                </p>
                <label className="btn btn-secondary" style={{ cursor: "pointer", display: "inline-block" }}>
                  📤 Import Backup (JSON)
                  <input type="file" accept=".json" style={{ display: "none" }} onChange={handleImportData} />
                </label>
              </div>
              <div style={{ padding: "15px", background: "var(--bg-secondary)", borderRadius: "8px" }}>
                <strong>Reset Transactions</strong>
                <p style={{ margin: "8px 0", color: "var(--text-secondary)", fontSize: "14px" }}>
                  Delete all transaction records while keeping inventory intact.
                </p>
                <button className="btn btn-danger" onClick={handleResetTransactions}>
                  🗑️ Clear All Transactions
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Modals — rendered as fixed overlays above everything */}
      {showModal && (
        <div className="modal-overlay">
          <div className="item-modal-content">
            <div className="item-modal-header">
              <div>
                <h2 className="item-modal-title">
                  {editingItem ? "Edit Item" : "Add New Item"}
                </h2>
                <p className="item-modal-subtitle">
                  Use a checkout-style USB barcode scanner first, then fill in the item details.
                </p>
              </div>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowModal(false)}
                aria-label="Close item form"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="item-modal-form">
              <div className="item-modal-grid">
                <div className="item-modal-panel">
                  <div className="item-modal-panel-header">
                    <div className="item-modal-step">Step 1</div>
                    <div className="item-modal-panel-title">Barcode</div>
                    <div className="item-modal-panel-subtitle">
                      {showParentBarcodeField
                        ? "Designed for USB barcode scanners like supermarket and self-checkout counters."
                        : "Sellable barcodes live in Packaging Units. The parent record is internal only."}
                    </div>
                  </div>

                  {showParentBarcodeField ? (
                    <>
                      <div className="item-modal-barcode">
                        <label htmlFor="item-barcode">Barcode *</label>
                        <div className="item-modal-barcode-row">
                          <input
                            ref={barcodeInputRef}
                            id="item-barcode"
                            type="text"
                            name="barcode"
                            value={formData.barcode}
                            onChange={handleInputChange}
                            onKeyPress={handleBarcodeScan}
                            placeholder="Focus here, then scan with USB barcode scanner"
                            required={showParentBarcodeField}
                            autoFocus
                          />
                          <div
                            className={
                              formData.barcode
                                ? "item-modal-chip success"
                                : "item-modal-chip"
                            }
                          >
                            {formData.barcode ? "Captured" : "Waiting"}
                          </div>
                        </div>
                        <small className="form-tip">
                          Tip: most USB scanners act like a keyboard and press Enter automatically after the scan.
                        </small>
                      </div>

                      <div className="item-modal-usb-note">
                        <div className="item-modal-usb-note-title">
                          Recommended for POS
                        </div>
                        <div className="item-modal-usb-note-copy">
                          Keep the cursor in the barcode field and scan. This is the standard flow for store counters.
                        </div>
                      </div>

                      <div className="item-modal-scanner-toggle">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowCameraFallback((value) => !value)}
                        >
                          {showCameraFallback
                            ? "Hide backup camera option"
                            : "Use backup camera scanner"}
                        </button>
                      </div>

                      {showCameraFallback && (
                        <div className="item-modal-scanner">
                          <BarcodeScanner
                            title="Backup Camera Scanner"
                            onScan={handleCameraScan}
                            onError={(err) => {
                              if (
                                err &&
                                !err.includes("NotFoundException") &&
                                !err.includes("No MultiFormat")
                              ) {
                                setError(`Scanner error: ${err}`);
                              }
                            }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="item-modal-usb-note">
                      <div className="item-modal-usb-note-title">
                        Packaging-unit barcode mode
                      </div>
                      <div className="item-modal-usb-note-copy">
                        Sellable barcodes live in Packaging Units below. The parent record stays internal and does not need a customer-facing barcode.
                      </div>
                    </div>
                  )}
                </div>

                <div className="item-modal-panel">
                  <div className="item-modal-panel-header">
                    <div className="item-modal-step">Step 2</div>
                    <div className="item-modal-panel-title">Product Details</div>
                    <div className="item-modal-panel-subtitle">
                      Name, selling price, cost, and category.
                    </div>
                  </div>

                  <div className="item-modal-fields">
                    <div className="form-group">
                      <label htmlFor="item-name">Item Name *</label>
                      <input
                        id="item-name"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="item-modal-two-col">
                      <div className="form-group">
                        <label htmlFor="item-sell-price">Sell Price *</label>
                        <CurrencyInput
                          id="item-sell-price"
                          className="numeric-entry"
                          name="price"
                          step="50"
                          value={formData.price}
                          onChange={(value) => handleInputChange({ target: { name: "price", value } })}
                          min="0"
                          label="sell price"
                          required
                          placeholder="Price to sell"
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor="item-base-price">Base Price</label>
                        <CurrencyInput
                          id="item-base-price"
                          className="numeric-entry"
                          name="base_price"
                          step="50"
                          value={formData.base_price}
                          onChange={(value) => handleInputChange({ target: { name: "base_price", value } })}
                          min="0"
                          label="base price"
                          placeholder="Cost/import price"
                        />
                      </div>
                    </div>

                    {formData.price && formData.base_price && (
                      <div className="item-modal-metrics">
                        <div className="item-modal-metric">
                          <div className="item-modal-metric-label">
                            Profit / unit
                          </div>
                          <div className="item-modal-metric-value">
                            MMK{" "}
                            {formatCurrency(
                              parseFloat(formData.price) -
                                parseFloat(formData.base_price || 0),
                            )}
                          </div>
                        </div>
                        <div className="item-modal-metric">
                          <div className="item-modal-metric-label">Margin</div>
                          <div className="item-modal-metric-value">
                            {(
                              ((parseFloat(formData.price) -
                                parseFloat(formData.base_price || 0)) /
                                parseFloat(formData.price)) *
                              100
                            ).toFixed(1)}
                            %
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="form-group">
                      <label htmlFor="item-category">Category</label>
                      <div className="item-modal-category-row">
                        <select
                          id="item-category"
                          name="category"
                          value={formData.category}
                          onChange={handleInputChange}
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.name}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                        {user?.role === "admin" && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowCategoryModal(true)}
                          >
                            + New
                          </button>
                        )}
                      </div>

                      {user?.role === "admin" && (
                        <div className="item-modal-category-quickadd">
                          <input
                            id="item-quick-category"
                            aria-label="Quick add category"
                            type="text"
                            placeholder="Quick add category"
                            value={newCategoryInput}
                            onChange={(e) =>
                              setNewCategoryInput(e.target.value)
                            }
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleQuickAddCategory();
                              }
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleQuickAddCategory}
                            disabled={!newCategoryInput.trim()}
                          >
                            Add
                          </button>
                        </div>
                      )}
                    </div>

                  </div>
                </div>
              </div>

              <div className="item-modal-packaging">
                <div className="item-modal-panel-header">
                  <div className="item-modal-step">Step 3</div>
                  <div className="item-modal-panel-title">Packaging Units</div>
                  <div className="item-modal-panel-subtitle">
                    Configure how this product is purchased and sold.
                  </div>
                </div>
                <ProductUnitsEditor
                  units={productUnits}
                  onChange={handleProductUnitsChange}
                  mainPrice={formData.price}
                  allowActive={Boolean(editingItem)}
                  disabled={loading}
                  showValidationErrors={showProductUnitValidation}
                />
              </div>

              <div className="item-modal-opening-stock">
                <div className="item-modal-panel-header">
                  <div className="item-modal-step">Step 4</div>
                  <div className="item-modal-panel-title">Opening Stock</div>
                  <div className="item-modal-panel-subtitle">
                    Enter the current quantity using the selected base unit.
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="item-stock">
                    Opening stock in {openingStockUnitName}
                  </label>
                  <input
                    id="item-stock"
                    type="number"
                    className="numeric-entry"
                    name="stock"
                    step="1"
                    value={formData.stock}
                    onChange={handleInputChange}
                    min="0"
                  />
                </div>
              </div>

              <div className="item-modal-footer">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading
                    ? "Saving..."
                    : editingItem
                      ? "Update Item"
                      : "Add Item"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showItemDetailsModal && selectedItem && (
        <div className="modal-overlay">
          <div className="modal item-details-modal">
            <div className="modal-header">
              <h2>Item Details</h2>
              <p>{selectedItem.name}</p>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowItemDetailsModal(false)}
                aria-label="Close item details"
              >
                &times;
              </button>
            </div>
            <div className="item-details-body">
              <div className="item-details-grid">
                {!selectedItem.unit_inventory_enabled && (
                  <div className="item-details-row">
                    <div className="item-details-label">Barcode</div>
                    <div className="item-details-value">
                      {selectedItem.barcode}
                    </div>
                  </div>
                )}
                <div className="item-details-row">
                  <div className="item-details-label">Sell Price</div>
                  <div className="item-details-value">
                    MMK {formatCurrency(selectedItem.price)}
                  </div>
                </div>
                <div className="item-details-row">
                  <div className="item-details-label">Base Price</div>
                  <div className="item-details-value">
                    {selectedItem.base_price
                      ? `MMK ${formatCurrency(selectedItem.base_price)}`
                      : "-"}
                  </div>
                </div>
                <div className="item-details-row">
                  <div className="item-details-label">Category</div>
                  <div className="item-details-value">
                    {selectedItem.category || "-"}
                  </div>
                </div>
                <div className="item-details-row">
                  <div className="item-details-label">Stock</div>
                  <div className="item-details-value">{selectedItem.stock}</div>
                </div>
              </div>

              {detailsUnits.length > 0 && (
                <div className="item-details-variants">
                  <div className="item-details-variants-title">Packaging Units</div>
                  <div className="item-details-variants-list">
                    {detailsUnits.map((unit) => (
                      <div
                        key={unit.id || unit.barcode}
                        className="item-details-variant-row item-details-unit-row"
                      >
                        <span className="idv-color item-details-unit-name">
                          {unit.unit_name}
                          {unit.is_base_unit && (
                            <span className="variant-primary-badge">Base unit</span>
                          )}
                          {!unit.is_active && (
                            <span className="item-details-unit-inactive">Inactive</span>
                          )}
                        </span>
                        <span className="idv-barcode">
                          {unit.barcode || "No barcode"}
                          {(unit.barcode_is_shared || unit.shared_barcode_count > 1) && (
                            <span className="unit-shared-barcode-note item-details-shared-barcode">
                              <span className="unit-shared-barcode-badge">Shared barcode</span>
                              {unit.shared_barcode_count > 1 && (
                                <span>{unit.shared_barcode_count} products</span>
                              )}
                            </span>
                          )}
                        </span>
                        <span className="idv-stock item-details-unit-factor">
                          {unit.conversion_factor} base unit{Number(unit.conversion_factor) === 1 ? "" : "s"}
                        </span>
                        <span className="item-details-unit-price">
                          MMK {formatCurrency(unit.selling_price)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      )}

      {showUserModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Register New User</h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowUserModal(false)}
                aria-label="Close user registration"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleUserSubmit}>
              <div className="form-group">
                <label htmlFor="new-user-username">Username *</label>
                <input
                  id="new-user-username"
                  type="text"
                  name="username"
                  value={userFormData.username}
                  onChange={(e) =>
                    setUserFormData({
                      ...userFormData,
                      username: e.target.value,
                    })
                  }
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-user-password">Password *</label>
                <input
                  id="new-user-password"
                  type="password"
                  name="password"
                  value={userFormData.password}
                  onChange={(e) =>
                    setUserFormData({
                      ...userFormData,
                      password: e.target.value,
                    })
                  }
                  required
                  minLength="6"
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-user-full-name">Full Name</label>
                <input
                  id="new-user-full-name"
                  type="text"
                  name="fullName"
                  value={userFormData.fullName}
                  onChange={(e) =>
                    setUserFormData({
                      ...userFormData,
                      fullName: e.target.value,
                    })
                  }
                  placeholder="Optional"
                />
              </div>
              <div className="form-group">
                <label htmlFor="new-user-role">Role *</label>
                <select
                  id="new-user-role"
                  name="role"
                  value={userFormData.role}
                  onChange={(e) =>
                    setUserFormData({ ...userFormData, role: e.target.value })
                  }
                  required
                >
                  <option value="cashier">Cashier</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div style={{ display: "flex", gap: "var(--sp-3)", marginTop: "var(--sp-5)" }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                  style={{ flex: 1 }}
                >
                  {loading ? "Creating..." : "Create User"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUserModal(false)}
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>
                {editingCategory ? "Edit Category" : "Create New Category"}
              </h2>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowCategoryModal(false)}
                aria-label="Close category form"
              >
                &times;
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveCategory();
              }}
            >
              <div className="form-group">
                <label htmlFor="category-name">Category Name *</label>
                <input
                  id="category-name"
                  type="text"
                  name="name"
                  value={categoryFormData.name}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      name: e.target.value,
                    })
                  }
                  required
                  autoFocus
                  placeholder="e.g., Fruits & Vegetables"
                />
              </div>
              <div className="form-group">
                <label htmlFor="category-description">Description</label>
                <input
                  id="category-description"
                  type="text"
                  name="description"
                  value={categoryFormData.description}
                  onChange={(e) =>
                    setCategoryFormData({
                      ...categoryFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Optional description"
                />
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading
                  ? "Saving..."
                  : editingCategory
                    ? "Update Category"
                    : "Create Category"}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowCategoryModal(false);
                  setEditingCategory(null);
                  setCategoryFormData({ name: "", description: "" });
                }}
              >
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Transaction Details Modal */}
      {showTransactionModal && selectedTransaction && (
        <div className="modal-overlay">
          <div
            className="modal modal-lg transaction-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="transaction-detail-title"
          >
            <div className="modal-header">
              <h2 id="transaction-detail-title">Transaction Details</h2>
              <p>
                Transaction ID:{" "}
                <code className="transaction-detail-id">
                  {selectedTransaction.transaction_id}
                </code>
              </p>
              <button
                type="button"
                className="close-btn"
                onClick={() => {
                  setShowTransactionModal(false);
                  setSelectedTransaction(null);
                }}
                aria-label="Close transaction details"
              >
                &times;
              </button>
            </div>
            <div className="transaction-detail-body">
              <div className="transaction-detail-summary">
                <div className="transaction-detail-metric">
                  <span>Date &amp; Time</span>
                  <strong>
                    {new Date(selectedTransaction.created_at).toLocaleString()}
                  </strong>
                </div>
                <div className="transaction-detail-metric is-total">
                  <span>Total Amount</span>
                  <strong>
                    MMK {formatCurrency(selectedTransaction.total_amount)}
                  </strong>
                </div>
                <div className="transaction-detail-metric">
                  <span>Items Count</span>
                  <strong>
                    {selectedTransaction.items?.length || 0} item(s)
                  </strong>
                </div>
              </div>

              <h3 className="transaction-items-heading">Items Sold</h3>
              {selectedTransaction.items &&
              selectedTransaction.items.length > 0 ? (
                <>
                  <div className="transaction-items-table-wrap">
                    <table className="transaction-items-table">
                      <thead>
                        <tr>
                          <th>Item Name</th>
                          <th>Barcode</th>
                          <th>Quantity</th>
                          <th>Unit Price</th>
                          <th>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedTransaction.items.map((item, index) => (
                          <tr key={`${item.barcode}-${index}`}>
                            <td>
                              {item.item_name}
                              <small>{getTransactionUnitSnapshot(item).unitName}</small>
                            </td>
                            <td>
                              <code>{item.barcode}</code>
                            </td>
                            <td>
                              {getTransactionUnitSnapshot(item).unitQuantity}
                              {getTransactionUnitSnapshot(item).conversionFactor > 1 && (
                                <small>{getTransactionUnitSnapshot(item).baseQuantity} base units</small>
                              )}
                            </td>
                            <td>MMK {formatCurrency(item.unit_price)}</td>
                            <td className="reports-amount">
                              MMK {formatCurrency(item.total_price)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="transaction-items-mobile">
                    {selectedTransaction.items.map((item, index) => (
                      <article
                        className="transaction-item-card"
                        key={`${item.barcode}-${index}`}
                      >
                        <div className="transaction-item-card-heading">
                          <strong>{item.item_name}</strong>
                          <span>{getTransactionUnitSnapshot(item).unitName}</span>
                          <code>{item.barcode}</code>
                        </div>
                        <dl>
                          <div>
                            <dt>Quantity</dt>
                            <dd>{getTransactionUnitSnapshot(item).unitQuantity}</dd>
                          </div>
                          <div>
                            <dt>Unit price</dt>
                            <dd>MMK {formatCurrency(item.unit_price)}</dd>
                          </div>
                          <div>
                            <dt>Total</dt>
                            <dd>MMK {formatCurrency(item.total_price)}</dd>
                          </div>
                        </dl>
                      </article>
                    ))}
                  </div>
                </>
              ) : (
                <p className="transaction-items-empty">
                  No items found in this transaction
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
